import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useLang } from "@/i18n";
import { useAuth } from "@/contexts/AuthContext";
import Logo from "@/components/Logo";
import LanguageDropdown from "@/components/nav/LanguageDropdown";
import MobileDrawer from "@/components/layout/MobileDrawer";
import HubAppSwitcher from "@/components/hub/HubAppSwitcher";
import {
  LogOut, LayoutDashboard, Shield, UserCircle2, CalendarDays, Grid3x3, Menu, Activity, Wrench,
} from "lucide-react";

// URGENT-MOBILE-HEADER-V1 — Header redesign for mobile / portrait
// tablet. Two distinct layouts coexist:
//
//   • < md (mobile + portrait tablet): a clean three-column row —
//        [hamburger] · [centered logo] · [spacer]
//     Everything else (Hub, login, register, dashboard, calendar,
//     language picker, logout) lives in a slide-out <MobileDrawer/>.
//
//   • md+ (landscape tablet + desktop): the previous layout, untouched.
//     This is what José explicitly asked to preserve.
//
// Helpers below are unchanged from the prior implementation.
const STAFF_ROLE_KEYS = new Set([
  "admin",
  "staff",
  "qa_manager",
  "qa_tester",
  "institutional_observer",
]);
const resolveIdentityRole = (user, t) => {
  if (!user) return "";
  if (STAFF_ROLE_KEYS.has(user.role)) {
    const translated = t(`roles.${user.role}`);
    if (translated && translated !== `roles.${user.role}`) return translated;
  }
  if (user.profile_key) {
    const translated = t(`profiles.${user.profile_key}`);
    if (translated && translated !== `profiles.${user.profile_key}`) return translated;
  }
  return user.role || "";
};

const Navbar = () => {
  const { t, lang } = useLang();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const myRacketsLabel = lang === "en" ? "My rackets" : "Mis raquetas";

  const onLogout = () => { logout(); navigate("/"); };

  return (
    <header className="sticky top-0 z-40 tsw-glass border-b border-slate-100">
      {/* MOBILE LAYOUT — visible until md (768px). */}
      <div className="md:hidden tsw-section h-16 grid grid-cols-3 items-center" data-testid="navbar-mobile">
        {/* Left col: hamburger */}
        <div className="flex justify-start">
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            className="inline-flex items-center justify-center w-10 h-10 rounded-full border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 transition"
            data-testid="nav-menu-toggle"
            aria-label={t("common.menu") === "common.menu" ? "Menu" : t("common.menu")}
            aria-expanded={drawerOpen}
          >
            <Menu className="w-5 h-5" />
          </button>
        </div>
        {/* Centre col: centered logo + brand text. The Logo component
            already wraps everything in a single <Link to="/">. We keep
            `size={40}` and the standard caption so the bar reads
            "[logo] TEAMSPORTWORLD / PILOT · VALIDATION" centred. */}
        <div className="flex justify-center">
          <Logo size={40} />
        </div>
        {/* Right col: empty placeholder — kept so the centre column
            stays mathematically centred. */}
        <div className="flex justify-end" aria-hidden />
      </div>

      {/* DESKTOP LAYOUT — md+. Preserved verbatim except minor padding
          on Register button. */}
      <div className="hidden md:flex tsw-section items-center justify-between h-16 gap-2">
        <Logo />

        <nav className="flex items-center gap-1">
          <NavLink to="/" end className={({isActive}) => `tsw-btn-ghost ${isActive ? "text-emerald-700 bg-emerald-50" : ""}`} data-testid="nav-home">
            {t("common.home")}
          </NavLink>
          <NavLink to="/apps" className={({isActive}) => `tsw-btn-ghost ${isActive ? "text-emerald-700 bg-emerald-50" : ""}`} data-testid="nav-apps">
            <Grid3x3 className="w-4 h-4" /> {t("apps.heroBadge")}
          </NavLink>
          {user && (
            <NavLink to="/app" className={({isActive}) => `tsw-btn-ghost ${isActive ? "text-emerald-700 bg-emerald-50" : ""}`} data-testid="nav-dashboard">
              <LayoutDashboard className="w-4 h-4" /> {t("common.dashboard")}
            </NavLink>
          )}
          {user && (
            user.profile_key === "player" ? (
              // RACKET-LAB-PLAYER-EQUIPMENT-UX-3 (Feb 2026) — for Player
              // profile the top "Mi calendario" entry is swapped for a
              // "Mis raquetas" shortcut. Updated by
              // DASHBOARD-SIDEMENU-PROFILES-1-FIX1 to point at the
              // standalone `/app/my-rackets` page instead of the legacy
              // dashboard anchor.
              <NavLink
                to="/app/my-rackets"
                className={({isActive}) => `tsw-btn-ghost ${isActive ? "text-emerald-700 bg-emerald-50" : ""}`}
                data-testid="nav-my-rackets"
              >
                <Wrench className="w-4 h-4" /> {myRacketsLabel}
              </NavLink>
            ) : (
              <NavLink to="/app/calendar" className={({isActive}) => `tsw-btn-ghost ${isActive ? "text-emerald-700 bg-emerald-50" : ""}`} data-testid="nav-my-calendar">
                <CalendarDays className="w-4 h-4" /> {t("calendar.title")}
              </NavLink>
            )
          )}
          {user && (user.role === "admin" || user.role === "staff") && (
            <NavLink to="/admin" className={({isActive}) => `tsw-btn-ghost ${isActive ? "text-emerald-700 bg-emerald-50" : ""}`} data-testid="nav-admin">
              <Shield className="w-4 h-4" /> {t("common.admin")}
            </NavLink>
          )}
          {/* Phase A6-prod (Feb 2026) — Racket Lab queda admin-only en
              Producción. El backend ya enforza el gate via
              HUB_REQUIRE_RACKET_LAB_MEMBERSHIP con bypass admin/staff;
              aquí exponemos el acceso UI sólo a esos roles para que
              tengan un punto de entrada visible para revisión interna
              controlada. Usuarios normales nunca ven este link. */}
          {user && (user.role === "admin" || user.role === "staff") && (
            <NavLink
              to="/apps/racket-lab"
              className={({isActive}) => `tsw-btn-ghost ${isActive ? "text-emerald-700 bg-emerald-50" : ""}`}
              data-testid="nav-racket-lab"
            >
              <Activity className="w-4 h-4" /> Racket Lab
            </NavLink>
          )}
        </nav>

        <div className="flex items-center gap-2">
          {/* F4 HUB nav switcher — gated by REACT_APP_HUB_NAV_ENABLED,
              non-admin authenticated users only (component self-gates). */}
          <HubAppSwitcher />
          {user && (
            <div
              className="flex items-center gap-2 rounded-full border border-slate-200 bg-white/80 px-3 py-1.5 text-xs font-medium text-slate-700 max-w-[260px]"
              data-testid="header-user-identity"
              title={`${user.full_name || user.email} — ${resolveIdentityRole(user, t)}`}
            >
              <UserCircle2 className="w-4 h-4 text-emerald-700 shrink-0" />
              <span className="truncate" data-testid="header-user-name">{user.full_name || user.email}</span>
              <span className="text-slate-300">—</span>
              <span className="text-emerald-700 whitespace-nowrap" data-testid="header-user-role">
                {resolveIdentityRole(user, t)}
              </span>
            </div>
          )}
          <div data-testid="lang-switch">
            <LanguageDropdown variant="navbar" testIdPrefix="lang" />
          </div>

          {!user ? (
            <>
              <Link to="/login" className="tsw-btn-ghost" data-testid="nav-login">{t("common.login")}</Link>
              <Link to="/register" className="tsw-btn-primary text-sm" data-testid="nav-register">
                {t("common.register")}
              </Link>
            </>
          ) : (
            <button onClick={onLogout} className="tsw-btn-logout" data-testid="nav-logout">
              <LogOut className="w-4 h-4" /> {t("common.logout")}
            </button>
          )}
        </div>
      </div>

      {/* Mobile drawer — outside the layout containers so it can take
          over the full viewport with its own backdrop. */}
      <MobileDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />
    </header>
  );
};

export default Navbar;
